import CallModal from "@/components/Call/CallModal";
import OnlineStatusContext from "@/context/online-status";
import getPublicUrl from "@/libs/get-public-url";
import { callStore } from "@/libs/stores/call.store";
import { creatorStore } from "@/libs/stores/creator.store";
import timeSyncService from "@/libs/timeSync";
import {
  Avatar,
  Box,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useMemo, useState } from "react";
import { FiPhone, FiUser, FiVideo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

// 6 hours
const ONLINE_TTL_MS = 6 * 60 * 60 * 1000;

function useIsOnline(creatorId, onlineStatus) {
  const lastActiveAt = onlineStatus?.[creatorId];
  const serverTimeMs = timeSyncService.getServerTimeSeconds() * 1000;
  return lastActiveAt != null && serverTimeMs - lastActiveAt < ONLINE_TTL_MS;
}

const CallCreatorAvatar = observer(({ callStatus }) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCallType, setSelectedCallType] = useState("video");
  const { onlineStatus, updateListenList } = useContext(OnlineStatusContext);
  const creatorId = callStatus?.creatorId;
  const creator = creatorStore.getCreator(creatorId);
  const isOnline = useIsOnline(creatorId, onlineStatus);

  useEffect(() => {
    if (creatorId && updateListenList) {
      updateListenList([creatorId]);
    }
  }, [creatorId, updateListenList]);

  const creatorWithStatus = useMemo(
    () =>
      creator
        ? {
            ...creator,
            creatorId: creator.id || creatorId,
            ...callStatus,
          }
        : { creatorId, ...callStatus },
    [creator, callStatus, creatorId]
  );

  const voicePrice = callStatus?.voicePricePerMinute ?? 500;
  const videoPrice = callStatus?.videoPricePerMinute ?? 1000;
  const callType = callStatus?.callType || "video";
  const isCallingThis = callStore.currentCall?.creatorId === creatorId;

  const handleMenuClick = (type) => {
    setSelectedCallType(type);
    onOpen();
  };

  const avatarSrc = creator?.picture ? getPublicUrl(creator.picture) : undefined;
  const isBusy = !!callStatus?.busy;
  const isOffline = !isOnline && !isCallingThis && !isOpen;
  const isDisabled = isOffline || isBusy;

  const tooltipLabel = isBusy
    ? "通話中"
    : isOffline
      ? "創作者目前離線"
      : creator?.displayName || "";

  return (
    <>
      <Menu>
        <Tooltip label={tooltipLabel} placement="top">
          <MenuButton
            as={Box}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            position="relative"
            display="inline-block"
          >
            <Avatar
              size="lg"
              src={avatarSrc}
              name={creator?.displayName}
              opacity={isOffline ? 0.5 : 1}
              filter={isOffline ? "grayscale(100%)" : undefined}
              transition="opacity 0.2s, filter 0.2s"
              borderWidth={isOffline ? 0 : "3px"}
              borderColor={isBusy ? "orange.400" : "green.500"}
              borderStyle="solid"
            />
            {isBusy && (
              <Box
                position="absolute"
                bottom="0"
                left="50%"
                transform="translateX(-50%)"
                bg="orange.500"
                color="white"
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="2xs"
                fontWeight="bold"
                whiteSpace="nowrap"
                zIndex={1}
              >
                通話中
              </Box>
            )}
            {!isBusy && (
              <Box
                position="absolute"
                bottom="-2px"
                right="-2px"
                bg="primary.100"
                p={0.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <Icon as={FiPhone} boxSize={3} color="green.400" />
                {callType === "video" && (
                  <Icon as={FiVideo} boxSize={3} color="green.400" />
                )}
              </Box>
            )}
          </MenuButton>
        </Tooltip>
        <MenuList
          bg="primary.100"
          color="white"
          border="1px solid"
          borderColor="secondary.500"
          borderRadius={8}
          py={0}
          minW="160px"
        >
          <MenuItem
            icon={<FiPhone />}
            onClick={() => !isDisabled && handleMenuClick("voice")}
            isDisabled={isDisabled}
            opacity={isDisabled ? 0.6 : 1}
            bg="whiteAlpha.100"
            _hover={{ bg: "whiteAlpha.200" }}
            _active={{ bg: "whiteAlpha.300" }}
          >
            <Flex justify="space-between" w="full" align="center">
              <Text>語音通話</Text>
              <Text fontSize="sm" color="whiteAlpha.800">
                💎{voicePrice}/分鐘
              </Text>
            </Flex>
          </MenuItem>
          {callType === "video" && (
            <MenuItem
              icon={<FiVideo />}
              onClick={() => !isDisabled && handleMenuClick("video")}
              isDisabled={isDisabled}
              opacity={isDisabled ? 0.6 : 1}
              bg="whiteAlpha.100"
              _hover={{ bg: "whiteAlpha.200" }}
              _active={{ bg: "whiteAlpha.300" }}
            >
              <Flex justify="space-between" w="full" align="center">
                <Text>視訊通話</Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  💎{videoPrice}/分鐘
                </Text>
              </Flex>
            </MenuItem>
          )}
          <MenuItem
            icon={<FiUser />}
            onClick={() => creator?.slug && navigate(`/c/@${creator.slug}`)}
            bg="whiteAlpha.100"
            _hover={{ bg: "whiteAlpha.200" }}
            _active={{ bg: "whiteAlpha.300" }}
          >
            <Text>前往個人頁</Text>
          </MenuItem>
        </MenuList>
      </Menu>
      <CallModal
        isOpen={isOpen}
        onClose={onClose}
        creator={creatorWithStatus}
        callType={selectedCallType}
      />
    </>
  );
});

const CallAvailableRow = observer(() => {
  var creators = Array.from(callStore.availableCreators.values());
  if (creators.length === 0 || callStore.loadingAvailable) {
    return null;
  }

  return (
    <Box mt={4} pl={2} pr={6}>
      <Text
        fontSize="xl"
        maxW="80%"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
        justify="space-between"
        fontWeight="bold"
        color="white"
        mb={3}
      >
        即時聊天創作者
      </Text>
      <Flex gap={4} overflowX="auto" pb={2} alignItems="center">
        {creators.map((callStatus) => (
          <CallCreatorAvatar key={callStatus.creatorId} callStatus={callStatus} />
        ))}
      </Flex>
    </Box>
  );
});

export default CallAvailableRow;
